// src/components/hubspotConfig.jsx
// HubSpot Settings modal: pick which company types to sync, and which HubSpot
// users to add as CRM reps.
//
// CRITICAL: This component is the source of the label-vs-value bug. Company
// types in HubSpot have a friendly LABEL ("IND - Prospect") and an internal
// VALUE ("PROSPECT"). The filter sent to HubSpot must use the VALUE.
// We display the label but always store the value.

import { useState, useEffect } from "react";
import * as HS from "../hubspot.js";
import { hashPw, genTempPw, sSet } from "../helpers.js";
import { Btn, FI, Modal, Spin } from "./ui.jsx";

export function HubSpotConfig({allowedTypes, onSave, onClose, existingUsers, onUsersChange}) {
  // types is an array of internal VALUES (strings).
  // When the parent passes legacy {label, value} objects or strings, we normalize.
  const [types, setTypes] = useState(() =>
    (allowedTypes || []).map(t => typeof t === "string" ? t : (t.value || t.label || ""))
  );
  // hsTypes is the full list from HubSpot: [{label, value}, ...]
  const [hsTypes,    setHsTypes]    = useState([]);
  const [loadingHs,  setLoadingHs]  = useState(false);
  const [hsErr,      setHsErr]      = useState("");
  const [manualType, setManualType] = useState("");

  // HubSpot users
  const [hsUsers,    setHsUsers]    = useState([]);
  const [loadingUsr, setLoadingUsr] = useState(false);
  const [selUsers,   setSelUsers]   = useState([]);
  const [importMsg,  setImportMsg]  = useState("");

  const loadFromHubSpot = async () => {
    setLoadingHs(true); setHsErr("");
    try {
      const opts = await HS.getCompanyTypes();
      setHsTypes(opts || []);
    } catch (e) {
      setHsErr("Could not load from HubSpot: " + e.message);
    }
    setLoadingHs(false);
  };

  const loadHsUsers = async () => {
    setLoadingUsr(true);
    try {
      const owners = await HS.getOwners();
      // Filter out owners already in CRM
      const existingEmails = new Set((existingUsers || []).map(u => (u.email || "").toLowerCase()));
      const fresh = owners.filter(o => o.email && !existingEmails.has(o.email.toLowerCase()));
      setHsUsers(fresh);
    } catch (e) {
      console.warn("Could not load HubSpot users:", e.message);
    }
    setLoadingUsr(false);
  };

  useEffect(() => {
    loadFromHubSpot();
    loadHsUsers();
  }, []);

  const toggleType = (val) => {
    if (!val) return;
    setTypes(prev => prev.includes(val) ? prev.filter(t => t !== val) : [...prev, val]);
  };
  const addManual = () => {
    const t = manualType.trim();
    if (!t || types.includes(t)) return;
    setTypes(prev => [...prev, t]);
    setManualType("");
  };
  const removeType = (val) => setTypes(prev => prev.filter(t => t !== val));

  // Look up the display label for a stored value
  const labelFor = (val) => {
    const found = hsTypes.find(t => t.value === val);
    return found ? found.label : val;
  };

  const toggleUser = (u) => {
    setSelUsers(prev => prev.find(x => x.id === u.id) ? prev.filter(x => x.id !== u.id) : [...prev, u]);
  };

  const importSelectedUsers = () => {
    if (selUsers.length === 0) return;
    const newUsers = selUsers.map(o => ({
      id: "u_" + Date.now() + "_" + o.id,
      email: o.email.toLowerCase(),
      name: o.name || o.email.split("@")[0],
      passwordHash: hashPw(genTempPw()),
      isTemp: true,
      isAdmin: false,
      seeAll: false,
      hubspotOwnerId: o.id,
    }));
    const merged = [...(existingUsers || []), ...newUsers];
    sSet("crm_users", merged);
    if (onUsersChange) onUsersChange(merged);
    setImportMsg("Added " + newUsers.length + " user" + (newUsers.length !== 1 ? "s" : "") + " - they can sign in with their HubSpot email + temp password (admin can reset).");
    setSelUsers([]);
    // Refresh HubSpot user list to remove imported ones
    loadHsUsers();
    setTimeout(()=>setImportMsg(""), 8000);
  };

  return (
    <Modal title="⚙  HubSpot Configuration" onClose={onClose} wide>
      <div style={{display:"flex",flexDirection:"column",gap:24}}>

        {/* COMPANY TYPES SECTION */}
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--dark)",marginBottom:3}}>Company Types to Sync</div>
              <div style={{fontSize:12,color:"var(--muted)"}}>Only companies matching these types will be pulled from HubSpot.</div>
            </div>
            <Btn variant="soft" onClick={loadFromHubSpot} disabled={loadingHs} style={{fontSize:12,padding:"6px 14px"}}>
              {loadingHs ? <><Spin/> Loading...</> : "📥 Load from HubSpot"}
            </Btn>
          </div>

          {hsErr && <div style={{padding:"10px 14px",borderRadius:10,background:"#fdecea",color:"var(--red)",fontSize:13,marginBottom:10}}>{hsErr}</div>}

          {/* Currently selected */}
          <div style={{marginTop:12,marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>
              Currently Selected ({types.length})
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,minHeight:40,padding:10,borderRadius:10,background:"var(--warm1)",border:"1px solid var(--warm2)"}}>
              {types.length === 0
                ? <span style={{fontSize:12,color:"var(--muted)",fontStyle:"italic",padding:"4px 8px"}}>None - sync will pull NO companies. Pick at least one type below.</span>
                : types.map(v => (
                  <span key={v} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 12px",borderRadius:99,background:"var(--brown)",color:"#fff",fontSize:12,fontWeight:600}}>
                    {labelFor(v)}
                    <button onClick={()=>removeType(v)} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:14,lineHeight:1,padding:"0 0 0 2px",opacity:.7}} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity=".7"}>×</button>
                  </span>
                ))
              }
            </div>
          </div>

          {/* Available from HubSpot */}
          {hsTypes.length > 0 && (
            <div>
              <div style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>
                Available in HubSpot ({hsTypes.length})
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:220,overflowY:"auto",padding:10,borderRadius:10,border:"1px solid var(--warm2)",background:"#fff"}}>
                {hsTypes.map(t => {
                  const v = t.value;
                  const lbl = t.label;
                  const isSel = types.includes(v);
                  return (
                    <label key={v} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 14px",borderRadius:10,border:"1.5px solid " + (isSel ? "var(--brown)" : "var(--warm2)"),background:isSel ? "var(--warm1)" : "#fff",cursor:"pointer",fontSize:13,fontWeight:500,transition:"all .12s"}}>
                      <input type="checkbox" checked={isSel} onChange={()=>toggleType(v)} style={{accentColor:"var(--brown)"}}/>
                      {lbl}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Manual entry */}
          <div style={{marginTop:14,padding:14,borderRadius:10,background:"var(--warm1)",border:"1px dashed var(--warm3)"}}>
            <div style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>
              Add custom type (advanced)
            </div>
            <div style={{display:"flex",gap:8}}>
              <div style={{flex:1}}>
                <FI label="" value={manualType} onChange={setManualType} placeholder="Type the exact internal VALUE (e.g. PROSPECT)"/>
              </div>
              <Btn variant="soft" onClick={addManual} disabled={!manualType.trim()}>Add</Btn>
            </div>
            <div style={{fontSize:11,color:"var(--muted)",marginTop:6,fontStyle:"italic"}}>
              Use this only if you know HubSpot's exact internal value. Otherwise pick from the list above.
            </div>
          </div>
        </div>

        {/* HUBSPOT USERS SECTION */}
        <div style={{paddingTop:20,borderTop:"1.5px solid var(--warm2)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--dark)",marginBottom:3}}>Add HubSpot Users to CRM</div>
              <div style={{fontSize:12,color:"var(--muted)"}}>Select HubSpot users to add as sales reps. Already-added users are hidden.</div>
            </div>
            <Btn variant="soft" onClick={loadHsUsers} disabled={loadingUsr} style={{fontSize:12,padding:"6px 14px"}}>
              {loadingUsr ? <><Spin/> Loading...</> : "🔄 Refresh"}
            </Btn>
          </div>

          {importMsg && <div style={{padding:"10px 14px",borderRadius:10,background:"#e6f4ed",color:"var(--green)",fontSize:13,fontWeight:500,marginBottom:10}}>{importMsg}</div>}

          {hsUsers.length === 0
            ? <div style={{padding:"20px 0",textAlign:"center",color:"var(--muted)",fontSize:13,fontStyle:"italic"}}>
                {loadingUsr ? "Loading HubSpot users..." : "All HubSpot users have been added, or HubSpot returned no users."}
              </div>
            : (
              <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:240,overflowY:"auto",padding:10,borderRadius:10,border:"1px solid var(--warm2)",background:"#fff"}}>
                {hsUsers.map(u => {
                  const isSel = !!selUsers.find(x => x.id === u.id);
                  return (
                    <label key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:8,border:"1.5px solid " + (isSel ? "var(--brown)" : "var(--warm2)"),background:isSel ? "var(--warm1)" : "#fff",cursor:"pointer",fontSize:13}}>
                      <input type="checkbox" checked={isSel} onChange={()=>toggleUser(u)} style={{accentColor:"var(--brown)"}}/>
                      <div style={{width:28,height:28,borderRadius:99,background:"var(--brown)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0}}>
                        {(u.name || u.email)[0].toUpperCase()}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600}}>{u.name || u.email.split("@")[0]}</div>
                        <div style={{fontSize:11,color:"var(--muted)"}}>{u.email}</div>
                      </div>
                      <span style={{fontSize:10,color:"var(--muted)",padding:"2px 7px",borderRadius:99,background:"var(--warm1)",fontFamily:"monospace"}}>HS#{u.id}</span>
                    </label>
                  );
                })}
              </div>
            )
          }

          {selUsers.length > 0 && (
            <div style={{marginTop:12,display:"flex",gap:10,alignItems:"center"}}>
              <Btn onClick={importSelectedUsers}>+ Add {selUsers.length} Selected User{selUsers.length !== 1 ? "s" : ""}</Btn>
              <Btn variant="ghost" onClick={()=>setSelUsers([])}>Clear Selection</Btn>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:16,borderTop:"1.5px solid var(--warm2)"}}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={()=>onSave(types)}>💾 Save & Sync from HubSpot</Btn>
        </div>
      </div>
    </Modal>
  );
}
